import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from './useAuth';

/**
 * Global user profile from GET /users/me.
 *
 * `is_operator` is the global super-admin flag (not derived from farm_users
 * membership), so it is available even for a user with zero farms — which is
 * what gates first-farm creation during onboarding.
 */
export interface UserProfile {
  id: string;
  email: string;
  name?: string | null;
  phone?: string | null;
  avatar_url?: string | null;
  is_operator: boolean;
  farms: { farm_id: string; farm_name: string; role: string }[];
}

async function fetchProfile(): Promise<UserProfile> {
  return api.get<UserProfile>('/api/v1/users/me');
}

export function useProfile() {
  const { session, user } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: fetchProfile,
    enabled: !!session && !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    profile: data ?? null,
    isOperator: data?.is_operator ?? false,
    isLoading,
    error,
  };
}
